/**
 * Vehicle Image Service — upload, delete, reorder, and manage vehicle listing photos.
 *
 * Compression lives in `image-compress.ts`. This file owns slot identity,
 * staging, and the vehicle_images row. Bytes never go to Storage except
 * through that pipeline, and a row is never written until the object exists.
 *
 * Class-based singleton per CLAUDE.md architecture rules.
 */
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import { jsonObject } from '../types/db-rows'
import type { Row } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import type { VehicleImageItem } from '../types/vehicle.types'
import type {
  ListingPhotoCaptureSource,
  ListingPhotoRequirementId,
  StagedListingPhoto,
} from '../types/listing-photo.types'
import {
  getShowcasePosition,
  isRepeatablePhotoRequirement,
  listingPhotoSlotKey,
} from '../types/listing-photo.types'
import { generateBlurhash } from '../utils/blurhash'
import { assertObjectStored, compressImage } from '../utils/image-compress'
import { requireLiveUserId } from './session-identity'
import { stagedSourceKey } from '../utils/listing-photo-storage'
import {
  ensureLiveStorageSession,
  isAuthDeniedError,
  objectExistsInStorage,
  refreshSessionOnce,
  uploadBytesWithSessionRecovery,
} from '../utils/storage-verify'

const BUCKET = 'vehicles'

interface VehicleImageUploadInput {
  uri: string
  position: number
  /*
   * LOS TRES SON OBLIGATORIOS, y antes fingian ser opcionales.
   *
   * `ListingPhoto` --el unico tipo que produce estas entradas-- ya los exige a
   * los tres. La opcionalidad de aca abajo era residuo de cuando existia una
   * subida "legacy" sin ranura, y sostenia una rama muerta que ademas estaba
   * ROTA: `requirement_id` es NOT NULL sin default en la base, asi que el
   * camino que insertaba `null` fallaba con 23502 en produccion. No lo veia
   * nadie porque los tipos generados estaban viejos y decian que aceptaba null.
   *
   * Ultima foto con `capture_source = 'legacy_upload'`: 2026-06-11. Desde
   * entonces todo entra por camara en vivo, con su ranura.
   */
  source: ListingPhotoCaptureSource
  capturedAt: string
  requirementId: ListingPhotoRequirementId
  width?: number
  height?: number
  /**
   * Bytes already sitting in the staging area, uploaded while the host was still shooting.
   * When present AND its `sourceUri` matches this photo's uri, upload becomes a server-side
   * COPY. On any mismatch or copy failure the normal upload runs — staging is an optimisation,
   * never a dependency.
   */
  staged?: StagedListingPhoto
}

// ─── Row Mapper ─────────────────────────────────────────────────────────────

function mapImageRow(row: Row<'vehicle_images'>): VehicleImageItem {
  return {
    id: row.id,
    vehicleId: row.vehicle_id,
    storagePath: row.storage_path,
    url: row.url,
    blurhash: row.blurhash,
    position: row.position,
    isPrimary: row.is_primary,
    width: row.width,
    height: row.height,
    captureSource: row.capture_source,
    capturedAt: row.captured_at,
    requirementId: row.requirement_id,
    photoSha256: row.photo_sha256,
    verificationPayload: jsonObject(row.verification_payload),
  }
}

function bytesToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

// ─── Service ────────────────────────────────────────────────────────────────

class VehicleImageServiceImpl {
  /**
   * Upload a single vehicle image.
   *
   * 1. Compress to 1920px JPEG
   * 2. Upload to Storage: vehicles/{vehicleId}/{uuid}.jpg
   * 3. Insert row into vehicle_images with position, dimensions
   *
   * @returns The inserted VehicleImageItem
   */
  /*
   * `image` ERA `string | VehicleImageUploadInput`, y con el string armaba
   * `{ uri, position }` -- sin ranura, sin origen, sin fecha de captura. Esa era
   * la puerta legacy, y no la llamaba nadie: la unica entrada real es
   * `uploadImages` desde review.tsx, que siempre manda un `ListingPhoto`
   * completo. Lo que si hacia era obligar a que los tres campos fueran
   * opcionales en todo el archivo, y de ahi colgaba la rama `insert` con
   * `requirement_id: null` que la base rechaza con 23502.
   *
   * Una firma que acepta menos de lo que la base exige no es flexibilidad: es
   * una promesa que el servidor no puede cumplir.
   */
  async uploadImage(
    vehicleId: string,
    input: VehicleImageUploadInput,
    position = 0,
    isPrimary = false,
  ): Promise<VehicleImageItem> {
    const repeatable = isRepeatablePhotoRequirement(input.requirementId)
    const effectivePosition = getShowcasePosition(input.requirementId, position)
    const effectiveIsPrimary =
      isPrimary || effectivePosition === 0 || input.requirementId === 'front_right'

    // SESSION GATE — first thing, before any write. Every path below touches
    // storage or PostgREST under RLS; a degraded session is what turned one
    // real host's publish into nine identical 403s in seven hours.
    await ensureLiveStorageSession()

    // FAST PATH: the bytes already travelled, while the host was still walking around the car.
    //
    // A staged record is honoured only when its `sourceKey` is the digest of this photo's uri —
    // a retake produced a new uri, so a stale record simply stops matching and the fresh bytes
    // win. The copy happens server-side: nothing moves over the phone's connection, which is
    // what turns the publish tap from seventeen uploads into seventeen metadata operations.
    //
    // Every failure falls through to the direct upload below. Staging can make publishing
    // faster; it must never be able to make it more broken than it was before staging existed.
    if (input.staged && input.staged.sourceKey === (await stagedSourceKey(input.uri))) {
      const staged = input.staged
      const destinationPath = `${vehicleId}/${listingPhotoSlotKey(
        input.requirementId,
        repeatable ? staged.photoSha256 : null,
      )}.jpg`
      const promoted = await this.promoteStagedObject(staged.stagedPath, destinationPath)
      if (promoted && (await objectExistsInStorage(BUCKET, destinationPath))) {
        return this.insertImageRow(vehicleId, destinationPath, {
          position: effectivePosition,
          isPrimary: effectiveIsPrimary,
          blurhash: staged.blurhash,
          width: staged.width,
          height: staged.height,
          sizeBytes: staged.sizeBytes,
          photoSha256: staged.photoSha256,
          source: input.source,
          capturedAt: input.capturedAt,
          requirementId: input.requirementId,
          originalWidth: input.width ?? null,
          originalHeight: input.height ?? null,
        })
      }
    }

    const compressed = await compressImage(input.uri, 'listing')
    const bytes = compressed.bytes
    const blurhash = await generateBlurhash(compressed.uri)

    // SHA-256 for dedup/audit — computed DEFENSIVELY. expo-crypto's native digest
    // on Android (SDK 54) wants a TypedArray, exactly as its own docs show;
    // passing `.buffer` (a raw ArrayBuffer) threw on the native module and, being
    // a plain Error with no userMessageKey, silently broke EVERY vehicle photo
    // upload — the host only ever saw "No se pudieron subir las fotos" and got
    // stranded at a draft with 0 photos. The hash is metadata, never a gate:
    // pass the Uint8Array, and if hashing ever fails again, the upload still ships.
    let photoSha256: string | null = null
    try {
      const digest = await Crypto.digest(Crypto.CryptoDigestAlgorithm.SHA256, new Uint8Array(bytes))
      photoSha256 = bytesToHex(digest)
    } catch {
      photoSha256 = null
    }

    // 4. The key is the photo's IDENTITY, not a random id and not a batch position.
    //
    // Exactly-once slots: the slot is the identity, so a retry OVERWRITES and a retake
    // replaces. Repeatable accessory slots: the CONTENT is the identity — the first design
    // numbered extras by upload-batch position, and a retry uploads a FILTERED batch, so the
    // survivors renumbered and an extra could land on its sibling's key. If the hash failed
    // (it is metadata, never a gate), a repeatable photo takes a random identity: at worst
    // that orphans an object, and it can never clobber a sibling. Images with no requirement
    // keep a random key: they have no slot to be idempotent about.
    const fallbackIdentity = Crypto.randomUUID().replace(/-/g, '')
    const storagePath = `${vehicleId}/${listingPhotoSlotKey(
      input.requirementId,
      repeatable ? (photoSha256 ?? fallbackIdentity) : null,
    )}.jpg`

    // 4.5 HEAL: only a content-addressed repeatable slot proves these same bytes
    // already exist. Fixed slots name a requirement, not its bytes: retakes must
    // replace them before new capture metadata can be written.
    if (repeatable && photoSha256 && (await objectExistsInStorage(BUCKET, storagePath))) {
      return this.insertImageRow(vehicleId, storagePath, {
        position: effectivePosition,
        isPrimary: effectiveIsPrimary,
        blurhash,
        width: compressed.width,
        height: compressed.height,
        sizeBytes: bytes.length,
        photoSha256,
        source: input.source,
        capturedAt: input.capturedAt,
        requirementId: input.requirementId,
        originalWidth: input.width ?? null,
        originalHeight: input.height ?? null,
      })
    }

    // 5. Upload to Supabase Storage — session gate + one auth-denied recovery
    // retry inside. A 403 that survives the refresh is the only point at which
    // "cerrá sesión y volvé a entrar" is the truth.
    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      BUCKET,
      storagePath,
      bytes,
      {
        contentType: 'image/jpeg',
        // Overwrite deliberately: the key already identifies which photo this is.
        upsert: true,
      },
    )

    if (uploadError) {
      // The `vehicles` bucket RLS requires the vehicle row to exist AND be owned
      // by the LIVE auth.uid(). The most common real-host failure is a permission
      // / session mismatch (RLS 403), which is actionable ("sign out and back
      // in") — distinguish it from a transient network failure so the host isn't
      // left with a bare "sale error".
      const raw = uploadError.message ?? ''
      const isForbidden = isAuthDeniedError(uploadError)
      throw new ServiceError({
        message: `vehicle image upload failed (${storagePath}, ${bytes.length}b): ${raw}`,
        code: isForbidden ? 'VEHICLE_IMAGE_FORBIDDEN' : 'VEHICLE_IMAGE_UPLOAD_FAILED',
        statusCode: isForbidden ? 403 : 500,
        userMessageKey: isForbidden
          ? 'listing.photo_upload_forbidden'
          : 'listing.photo_upload_network',
        cause: uploadError,
      })
    }

    await assertObjectStored(BUCKET, storagePath)

    // 6–7. Public URL, DB row, sibling sweep — shared with the staged-copy path so the two can
    // never disagree about what a finished photo looks like in the database.
    return this.insertImageRow(vehicleId, storagePath, {
      position: effectivePosition,
      isPrimary: effectiveIsPrimary,
      blurhash,
      width: compressed.width,
      height: compressed.height,
      sizeBytes: bytes.length,
      photoSha256,
      source: input.source,
      capturedAt: input.capturedAt,
      requirementId: input.requirementId,
      originalWidth: input.width ?? null,
      originalHeight: input.height ?? null,
    })
  }

  /**
   * Move a staged object onto its final key, server-side. COPY ONLY — nothing is ever removed.
   *
   * The first version removed the destination before copying, because `copy` has no upsert.
   * The adversarial review caught what that costs: remove() succeeds, the connection drops,
   * copy fails, the direct-upload fallback fails on the same dead network — and the LIVE
   * listing's photo object is gone with its row still pointing at the key. A helper whose
   * failure mode is "destroys the thing it was optimising" is not an optimisation.
   *
   * So: copy, and nothing else. An occupied destination (edit-retake over a live photo, or a
   * retry whose object already landed) makes the copy fail — and the caller falls back to the
   * direct upload, whose `upsert: true` replaces ATOMICALLY on success and destroys nothing on
   * failure. A false here costs bandwidth, never a photo, and now that sentence is true.
   */
  private async promoteStagedObject(stagedPath: string, destinationPath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage.from(BUCKET).copy(stagedPath, destinationPath)
      return !error
    } catch {
      return false
    }
  }

  /**
   * Everything after the bytes are in place: public URL, the row, and the sibling sweep.
   *
   * THE ROW WRITE IS AN UPSERT ON THE SLOT IDENTITY, NOT AN INSERT.
   *
   * Insert-then-sweep was structurally incompatible with the unique index that guards these
   * rows: on an edit-retake the OLD row still exists when the new insert arrives, so the index
   * rejected it with 23505 before the sweep could ever run — every edit-retake failed, and the
   * rollback then deleted the storage object at the slot key, which for a slot-keyed listing
   * IS the live photo. The adversarial review caught it before a host did. ON CONFLICT UPDATE
   * is the write that says what we mean: this slot identity now holds these bytes.
   */
  private async insertImageRow(
    vehicleId: string,
    storagePath: string,
    meta: {
      position: number
      isPrimary: boolean
      blurhash: string | null
      width: number
      height: number
      sizeBytes: number
      photoSha256: string | null
      source: ListingPhotoCaptureSource
      capturedAt: string
      requirementId: ListingPhotoRequirementId
      originalWidth: number | null
      originalHeight: number | null
    },
  ): Promise<VehicleImageItem> {
    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath)
    const publicUrl = urlData.publicUrl

    // slot_index completes the identity: 0 for exactly-once slots (a retake replaces), a
    // 28-bit content-derived value for repeatable extras (a photo is always itself, whatever
    // the upload batch looked like). Mirrors how the storage key was derived above.
    const repeatable = isRepeatablePhotoRequirement(meta.requirementId)
    const slotIndex = repeatable
      ? parseInt(
          (meta.photoSha256 ?? storagePath.slice(storagePath.lastIndexOf('-') + 1)).slice(0, 7),
          16,
        ) || 0
      : 0

    const row = {
      vehicle_id: vehicleId,
      storage_path: storagePath,
      url: publicUrl,
      blurhash: meta.blurhash,
      position: meta.position,
      is_primary: meta.isPrimary,
      width: meta.width,
      height: meta.height,
      size_bytes: meta.sizeBytes,
      mime_type: 'image/jpeg',
      capture_source: meta.source,
      captured_at: meta.capturedAt,
      requirement_id: meta.requirementId,
      photo_sha256: meta.photoSha256,
      verification_payload: {
        source: meta.source,
        originalWidth: meta.originalWidth,
        originalHeight: meta.originalHeight,
        compressedWidth: meta.width,
        compressedHeight: meta.height,
      },
      slot_index: slotIndex,
    }

    // Siempre upsert. La rama `insert` existia para las fotos sin ranura y no
    // podia funcionar: `requirement_id` es NOT NULL en la base. Con la ranura
    // garantizada por el tipo, una sola escritura idempotente -- volver a
    // tomar la misma foto reemplaza, no duplica.
    // The storage write and the row write are two separate HTTP requests. A JWT can
    // expire between them (especially while the host is walking around taking 17
    // photos), so protecting only Storage is insufficient: the object lands and the
    // `RETURNING` upsert is then rejected by RLS. Retry the *row write* once with a
    // forced refresh, using the same idempotent conflict key so a retry can never
    // create a duplicate.
    const writeRow = async () =>
      supabase
        .from('vehicle_images')
        .upsert(row, { onConflict: 'vehicle_id,requirement_id,slot_index' })
        .select()
        .single()

    await ensureLiveStorageSession()
    let { data, error: dbError } = await writeRow()
    if (dbError && isAuthDeniedError(dbError)) {
      if (await refreshSessionOnce()) {
        const retry = await writeRow()
        data = retry.data
        dbError = retry.error
      }
    }

    if (dbError) {
      // Rollback ONLY for random-key uploads, where the object is a genuine orphan. For a
      // slot-keyed path the key is deterministic — removing it can only ever destroy the
      // slot's CURRENT photo, which on an edit is the live listing's image.
      if (!meta.requirementId) {
        await supabase.storage.from(BUCKET).remove([storagePath])
      }
      throw ServiceError.fromSupabaseError(dbError)
    }
    if (!data) {
      throw new ServiceError({
        message: 'Vehicle image upsert returned no row',
        code: 'VEHICLE_IMAGE_ROW_MISSING',
        statusCode: 500,
      })
    }

    // Sweep any sibling left by an older upload of this same slot.
    //
    // The deterministic key stops NEW duplicates, but a car that already collected two rows
    // before this change stays broken until one of them goes. INSERT FIRST, delete after:
    // reversed, a failure between the two leaves the slot empty and the host is asked to shoot
    // the photo again. Only non-repeatable slots — the accessory slots are meant to hold
    // several.
    if (meta.requirementId && !isRepeatablePhotoRequirement(meta.requirementId)) {
      await supabase
        .from('vehicle_images')
        .delete()
        .eq('vehicle_id', vehicleId)
        .eq('requirement_id', meta.requirementId)
        .neq('id', (data as { id: string }).id)
    }

    return mapImageRow(data)
  }

  /**
   * Upload one capture to the per-user staging area, in the background, while the host keeps
   * shooting.
   *
   * WHY. Publishing used to move all seventeen photos in one minute over a phone connection —
   * the exact minute that produced "No se pudo terminar de subir las fotos a tiempo" eight
   * times in twelve hours. The host spends ten-plus minutes walking around the car; that is
   * when the bytes should travel. The vehicle row does not exist yet (pricing comes later), so
   * the bucket's vehicle-keyed INSERT policy cannot apply — staging has its own policies, keyed
   * on `staging/{auth.uid()}/…`.
   *
   * The key embeds the content hash, so a retake stages a NEW object and the old one simply
   * becomes an orphan for the next sweep — numbering never has to match the final batch. The
   * hash is the identity here, so unlike the direct path, failing to compute it aborts staging
   * (returns null) rather than shipping under a random name: the direct path at publish still
   * computes its own hash defensively and remains the authority.
   *
   * Returns null on ANY failure. The caller records nothing and the photo publishes through
   * the direct path exactly as it would have before staging existed.
   */
  async stageListingPhoto(uri: string): Promise<StagedListingPhoto | null> {
    try {
      const liveUserId = await requireLiveUserId()
      const compressed = await compressImage(uri, 'listing')
      const blurhash = await generateBlurhash(compressed.uri)
      const digest = await Crypto.digest(
        Crypto.CryptoDigestAlgorithm.SHA256,
        new Uint8Array(compressed.bytes),
      )
      const photoSha256 = bytesToHex(digest)

      const stagedPath = `staging/${liveUserId}/${photoSha256}.jpg`
      const { error } = await uploadBytesWithSessionRecovery(BUCKET, stagedPath, compressed.bytes, {
        contentType: 'image/jpeg',
        upsert: true,
      })
      if (error) return null
      await assertObjectStored(BUCKET, stagedPath)

      return {
        sourceKey: await stagedSourceKey(uri),
        stagedPath,
        photoSha256,
        blurhash,
        width: compressed.width,
        height: compressed.height,
        sizeBytes: compressed.sizeBytes,
      }
    } catch {
      return null
    }
  }

  /**
   * Sweep the caller's staging area. Best-effort by design — an orphaned staged object costs
   * cents of storage and is removed by the next sweep; a publish or a discard must never fail
   * because cleanup could not run.
   */
  async clearStagedListingPhotos(): Promise<void> {
    try {
      const liveUserId = await requireLiveUserId()
      const prefix = `staging/${liveUserId}`
      const { data } = await supabase.storage.from(BUCKET).list(prefix, { limit: 100 })
      const names = (data ?? []).map((entry) => `${prefix}/${entry.name}`)
      if (names.length > 0) await supabase.storage.from(BUCKET).remove(names)
    } catch {
      // best effort only
    }
  }

  /**
   * Upload multiple images sequentially (memory-safe on low-end devices).
   * Tracks progress via callback.
   */
  async uploadImages(
    vehicleId: string,
    images: VehicleImageUploadInput[],
    onProgress?: (completed: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<VehicleImageItem[]> {
    const results: VehicleImageItem[] = []

    // No numbering pass any more. Extras of a repeatable slot used to be numbered by their
    // position in THIS batch — and a retry uploads a filtered batch, so the survivors
    // renumbered and an extra could land on its sibling's key. Identity now comes from the
    // photo's own content hash, computed inside uploadImage, which no batch composition can
    // change.

    for (let i = 0; i < images.length; i++) {
      // Checked at the TOP of each iteration. A caller-side timeout abandons the
      // promise but cannot stop this loop, so the submit banner told the host to
      // retry while the original loop kept uploading — and the retry, snapshotting
      // an uploadedPhotoUris list that only advances per COMPLETED upload, sent
      // the same photos again. vehicle_images has no unique key, so the duplicates
      // stick, and vehiclePhotosSchema then requires exactly one photo per slot:
      // that listing can never be edited or re-saved again.
      if (signal?.aborted) break
      const img = images[i]!
      const pos = img.requirementId
        ? getShowcasePosition(img.requirementId, img.position)
        : img.position
      const isPrimary = pos === 0 || img.requirementId === 'front_right'
      const item = await this.uploadImage(vehicleId, img, pos, isPrimary)
      results.push(item)
      // Report BEFORE the next abort check so a photo that actually landed is
      // always recorded — otherwise the retry guard forgets it and re-uploads it.
      onProgress?.(i + 1, images.length)
    }

    return results
  }

  /**
   * Delete a vehicle image — removes from Storage + deletes DB row.
   */
  async deleteImage(imageId: string): Promise<void> {
    // Get storage path first
    const { data: row, error: fetchError } = await supabase
      .from('vehicle_images')
      .select('storage_path')
      .eq('id', imageId)
      .single()

    if (fetchError) throw ServiceError.fromSupabaseError(fetchError)
    if (!row) throw ServiceError.notFound('Vehicle image')

    // Delete from storage
    const { error: storageError } = await supabase.storage.from(BUCKET).remove([row.storage_path])

    if (storageError) {
      throw new ServiceError({
        message: `Storage delete failed: ${storageError.message}`,
        code: 'STORAGE_DELETE_FAILED',
        statusCode: 500,
        cause: storageError,
      })
    }

    // Delete DB row
    const { error: dbError } = await supabase.from('vehicle_images').delete().eq('id', imageId)

    if (dbError) throw ServiceError.fromSupabaseError(dbError)
  }

  /**
   * Reorder images — updates position field for each image.
   * imageIds array is in the desired display order (index 0 = position 0 = primary).
   */
  async reorderImages(vehicleId: string, imageIds: string[]): Promise<void> {
    // Use a transaction-like approach: update each image's position + is_primary
    const updates = imageIds.map((id, index) => ({
      id,
      position: index,
      is_primary: index === 0,
    }))

    // Batch update via individual calls (Supabase doesn't support multi-row upsert on non-PK columns)
    const errors: string[] = []
    for (const update of updates) {
      const { error } = await supabase
        .from('vehicle_images')
        .update({
          position: update.position,
          is_primary: update.is_primary,
        })
        .eq('id', update.id)
        .eq('vehicle_id', vehicleId)

      if (error) errors.push(`${update.id}: ${error.message}`)
    }

    if (errors.length > 0) {
      throw new ServiceError({
        message: `Reorder failed for ${errors.length} image(s)`,
        code: 'REORDER_FAILED',
        statusCode: 500,
        details: { errors },
      })
    }
  }

  /**
   * Set a specific image as primary (position 0), shifting others.
   */
  async setPrimaryImage(vehicleId: string, imageId: string): Promise<void> {
    // Fetch all images for this vehicle, ordered
    const { data: images, error } = await supabase
      .from('vehicle_images')
      .select('id')
      .eq('vehicle_id', vehicleId)
      .order('position', { ascending: true })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!images || images.length === 0) throw ServiceError.notFound('Vehicle images')

    // Build new order: target first, then the rest in original order
    const ids = images.map((img) => img.id as string)
    const filtered = ids.filter((id) => id !== imageId)
    const newOrder = [imageId, ...filtered]

    await this.reorderImages(vehicleId, newOrder)
  }

  /**
   * Get all images for a vehicle, ordered by position.
   */
  async getImages(vehicleId: string): Promise<VehicleImageItem[]> {
    const { data, error } = await supabase
      .from('vehicle_images')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .order('position', { ascending: true })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapImageRow)
  }
}

// ─── Singleton Export ───────────────────────────────────────────────────────

export const vehicleImageService = new VehicleImageServiceImpl()
